import { useCallback, useEffect, useMemo } from 'react';
import { Navigate, generatePath, useLocation, useOutletContext } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import {
  clearFormsMsg,
  deleteSelectedMsg,
  deletingSelectedMsg,
  deselectAllMsg,
  t,
} from '../../../common/i18n/index.js';
import { OpenFormBar } from '../OpenFormComponents/OpenFormBar.js';
import { OpenFormGrid, OpenFormGridCol, OpenFormGridRow } from '../OpenFormComponents/OpenFormGrid.js';
import { useOpenFormList } from '../OpenFormHooks/useOpenFormList.js';
import { useRefSet } from '../OpenFormHooks/useRefSet.js';
import { type OpenFormOutlet } from '../OpenFormLayout/OpenFormLayoutOutlet.js';
import { useOpenFormAsync, useOpenFormState } from '../OpenFormProvider.js';
import { OpenFormListNavigation } from './OpenFormListNavigation.js';
import { OpenFormListTableDraft } from './OpenFormListTableDraft.js';
import { OpenFormListTableForm } from './OpenFormListTableForm.js';

export const OpenFormListLayout = () => {
  const { pathname, search } = useLocation();
  const { reload } = useOutletContext<Required<OpenFormOutlet>>();
  const { disabled } = useOpenFormState();
  const async = useOpenFormAsync();
  const ready = useOpenFormList();
  const [set, selected, select] = useRefSet();
  const count = useMemo(() => (selected.size ? `(${selected.size})` : ''), [selected]);

  const clearForms = useCallback(() => {
    selected.forEach(formId => async.store.removeItem(formId));
    reload();
  }, [reload, async, selected]);

  const deleteDrafts = useCallback(() => {
    async.fetch
      .deleteOpenFormDrafts({ referenceNumbers: Array.from(selected) })
      .resolved(clearForms)
      .rejected(text => async.actions.notification({ text, type: 'warning' }))
      .execute({ disable: true, message: t.WN64(deletingSelectedMsg) });
  }, [async, selected, clearForms]);

  useEffect(() => () => select(), [pathname, select]);

  switch (pathname) {
    case paths.OPENFORM_LIST_DRAFT:
      return async.flags.has('draft') ? (
        <OpenFormBar
          leftFunc={count ? select : undefined}
          leftIcon="undo"
          leftText={count ? `${count} ${t.C7AE(deselectAllMsg)}` : t.C7AE(deselectAllMsg)}
          rightFunc={count ? deleteDrafts : undefined}
          rightIcon="delete"
          rightText={count ? `${t.B1Q0(deleteSelectedMsg)} ${count}` : t.B1Q0(deleteSelectedMsg)}
        >
          {!ready ? null : (
            <OpenFormGrid className="of-openform__list">
              <OpenFormGridRow justifyCenter>
                <OpenFormGridCol colWidth={9}>
                  <OpenFormListNavigation disabled={disabled} pathname={pathname} />
                </OpenFormGridCol>
                <OpenFormGridCol colWidth={12}>
                  <OpenFormListTableDraft disabled={disabled} selected={selected} select={select} set={set} />
                </OpenFormGridCol>
              </OpenFormGridRow>
            </OpenFormGrid>
          )}
        </OpenFormBar>
      ) : (
        <OpenFormBar>
          {!ready ? null : <Navigate replace to={generatePath(paths.OPENFORM_LIST_FORM) + search} />}
        </OpenFormBar>
      );
    case paths.OPENFORM_LIST_FORM:
      return async.flags.has('draft') ? (
        <OpenFormBar>
          {!ready ? null : (
            <OpenFormGrid className="of-openform__list">
              <OpenFormGridRow justifyCenter>
                <OpenFormGridCol colWidth={9}>
                  <OpenFormListNavigation disabled={disabled} pathname={pathname} />
                </OpenFormGridCol>
                <OpenFormGridCol colWidth={12}>
                  <OpenFormListTableForm disabled={disabled} selected={selected} select={select} set={set} />
                </OpenFormGridCol>
              </OpenFormGridRow>
            </OpenFormGrid>
          )}
        </OpenFormBar>
      ) : (
        <OpenFormBar
          leftFunc={count ? select : undefined}
          leftIcon="undo"
          leftText={count ? `${count} ${t.C7AE(deselectAllMsg)}` : t.C7AE(deselectAllMsg)}
          rightFunc={count ? clearForms : undefined}
          rightIcon="delete"
          rightText={count ? `${t.KZJT(clearFormsMsg)} ${count}` : t.KZJT(clearFormsMsg)}
        >
          {!ready ? null : (
            <OpenFormGrid className="of-openform__list">
              <OpenFormGridRow justifyCenter>
                <OpenFormGridCol colWidth={12}>
                  <OpenFormListTableForm disabled={disabled} selected={selected} select={select} set={set} />
                </OpenFormGridCol>
              </OpenFormGridRow>
            </OpenFormGrid>
          )}
        </OpenFormBar>
      );
    default:
      return null;
  }
};
